import React from 'react';
import axios from 'axios';

/*
 * media.js — signed URLs for private uploads (WR-014).
 *
 * /uploads used to be served by express.static with no authentication at
 * all. It is now guarded, but an <img> tag cannot send an Authorization
 * header — so the backend also accepts a short-lived media token in the
 * query string, and this module is where the browser keeps one.
 *
 * The token is NOT the session JWT. Session tokens are long-lived and open
 * the whole API; putting one in a URL leaks it into history, referrers and
 * proxy logs. This one lasts 15 minutes and reads files, nothing else.
 *
 * Stored values are untouched: the database still holds "/uploads/x.png",
 * and the token is appended at render time.
 */

let token = null;
let expiresAt = 0;

// Components that render media re-run when the token first lands or rotates,
// so an image that mounted before the token arrived does not stay broken.
const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

let refreshTimer = null;

export async function refreshMediaToken() {
  try {
    const { data } = await axios.get('/media/token');
    token = data.token;
    // Renew at 80% of life so a request is never made with a token that
    // expires in flight.
    const lifetimeMs = (data.expiresIn || 900) * 1000;
    expiresAt = Date.now() + lifetimeMs;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshMediaToken, lifetimeMs * 0.8);
    notify();
    return token;
  } catch (err) {
    // Non-fatal: images degrade, the rest of the app is unaffected.
    console.error('Failed to obtain media token:', err);
    return null;
  }
}

export function clearMediaToken() {
  token = null;
  expiresAt = 0;
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  notify();
}

export function hasMediaToken() {
  return !!token && Date.now() < expiresAt;
}

/**
 * Sign an upload path for use in src/href.
 *
 * Pass anything: an absolute URL, an empty value, or a stored "/uploads/…"
 * path. Only same-origin upload paths are signed — appending the token to a
 * third-party URL would hand it to that host.
 */
export function mediaUrl(input) {
  if (!input) return input;
  const raw = String(input);

  let pathname = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (u.origin !== window.location.origin) return raw;   // never sign off-origin
      pathname = u.pathname + u.search;
    } catch { return raw; }
  }

  if (!pathname.startsWith('/uploads/')) return raw;
  if (!token) return pathname;

  return `${pathname}${pathname.includes('?') ? '&' : '?'}mt=${encodeURIComponent(token)}`;
}

/** Absolute form, for the few places that build `window.location.origin + …`. */
export function absoluteMediaUrl(input) {
  const signed = mediaUrl(input);
  if (!signed || /^https?:\/\//i.test(signed)) return signed;
  return `${window.location.origin}${signed}`;
}

/** Re-render the calling component when the media token changes. */
export function useMediaToken() {
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return token;
}
